import React, { createContext, useContext, useMemo, useState, useCallback, useRef } from "react";
import Joyride, { EVENTS, STATUS, ACTIONS } from "react-joyride";
import useTranslation from "../../hooks/useTranslation";

const TourContext = createContext({ startTour: () => {} });

export const useTour = () => useContext(TourContext);

const TourProvider = ({ children }) => {
  const { t } = useTranslation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const didShowAnyStepRef = useRef(false);

  const steps = useMemo(
    () => [
      {
        target: '[data-tour="header-logo"]',
        content: t("tour.header.logo", "Retournez à l’accueil à tout moment."),
        placement: "bottom",
      },
      {
        target: '[data-tour="header-search"]',
        content: t("tour.header.search", "Recherchez des contenus, chaînes et jeux ici."),
        placement: "bottom",
      },
      {
        target: '[data-tour="header-theme"]',
        content: t("tour.header.theme", "Changez le thème clair/sombre."),
        placement: "left",
      },
      {
        target: '[data-tour="header-language"]',
        content: t("tour.header.language", "Changez la langue de l’interface."),
        placement: "left",
      },
      {
        target: '[data-tour="header-account"]',
        content: t("tour.header.account", "Accédez à votre compte (connexion/profil)."),
        placement: "left",
      },
      {
        target: '[data-tour="nav-home"]',
        content: t("tour.sidebar.home", "Accueil : revenez ici quand vous voulez."),
        placement: "right",
      },
      {
        target: '[data-tour="nav-go-live"]',
        content: t("tour.sidebar.goLive", "Passez en direct pour démarrer un stream."),
        placement: "right",
      },
      {
        target: '[data-tour="nav-events"]',
        content: t("tour.sidebar.events", "Consultez les événements ici."),
        placement: "right",
      },
      {
        target: '[data-tour="nav-my-events"]',
        content: t("tour.sidebar.myEvents", "Gérez vos événements (visible uniquement connecté)."),
        placement: "right",
      },
    ],
    [t]
  );

  const startTour = useCallback(() => {
    didShowAnyStepRef.current = false;
    setStepIndex(0);
    setRun(true);
  }, []);

  const handleCallback = useCallback((data) => {
    const { status, type, index, action } = data;

    // Mark that we actually managed to render a step (target exists).
    if (type === EVENTS.STEP_BEFORE) {
      didShowAnyStepRef.current = true;
    }

    // If a step target doesn't exist, is hidden, or can't be positioned, skip it.
    if (type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + 1);
      return;
    }

    if (type === EVENTS.ERROR) {
      setStepIndex(index + 1);
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const delta = action === ACTIONS.PREV ? -1 : 1;
      setStepIndex(index + delta);
      return;
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Only mark as completed if the user actually saw at least one tooltip.
      if (didShowAnyStepRef.current) {
        localStorage.setItem("corearena.tour.completed", "1");
      }
      localStorage.removeItem("corearena.tour.pending");
      setRun(false);
      setStepIndex(0);
    }
  }, []);

  return (
    <TourContext.Provider value={{ startTour }}>
      <Joyride
        run={run}
        stepIndex={stepIndex}
        steps={steps}
        continuous
        scrollToFirstStep
        showSkipButton
        disableOverlayClose
        callback={handleCallback}
        locale={{
          back: t("common.previous", "Précédent"),
          close: t("common.close", "Fermer"),
          last: t("common.close", "Fermer"),
          next: t("common.next", "Suivant"),
          skip: t("common.skip", "Passer"),
        }}
        styles={{
          options: {
            zIndex: 2000
          }
        }}
      />
      {children}
    </TourContext.Provider>
  );
};

export default TourProvider;

